import { useState, ReactNode } from 'react';
import kit from './kit.module.css';
import styles from './PermissionScopingDemo.module.css';

type State = 'perm1' | 'perm2' | 'ready';

interface Permission {
  label: string;
  granted: boolean;
}

function AgentLine({ children }: { children: ReactNode }) {
  return (
    <div className={kit.agent}>
      <span className={kit.agentIcon} aria-hidden="true">✦</span>
      <p>{children}</p>
    </div>
  );
}

function GrantedChip({ label, granted, onEnable }: { label: string; granted: boolean; onEnable?: () => void }) {
  if (granted) {
    return <span className={`${kit.chip} ${kit.chipGood}`}>✓ {label}</span>;
  }
  return (
    <span className={`${kit.chip} ${styles.skipped}`}>
      {label}: skipped
      {onEnable && (
        <button className={styles.enableLink} onClick={onEnable}>
          Enable
        </button>
      )}
    </span>
  );
}

interface PermissionCardProps {
  step: number;
  title: string;
  reason: string;
  skipLabel: string;
  onAllow: () => void;
  onSkip: () => void;
}

function PermissionCard({ step, title, reason, skipLabel, onAllow, onSkip }: PermissionCardProps) {
  return (
    <div className={kit.proposal}>
      <div className={kit.proposalTop}>
        <span>Permission request</span>
        <span>0{step} / 02</span>
      </div>
      <h3 className={kit.proposalTitle}>{title}</h3>
      <dl className={kit.fields}>
        <dt>Why</dt>
        <dd>{reason}</dd>
        <dt>Scope</dt>
        <dd>Only this, nothing more</dd>
      </dl>
      <div className={`${kit.row} ${styles.actions}`}>
        <button className={`${kit.btn} ${kit.btnApprove}`} onClick={onAllow}>
          Allow
        </button>
        <button className={`${kit.btn} ${kit.btnGhost}`} onClick={onSkip}>
          {skipLabel}
        </button>
      </div>
    </div>
  );
}

export function PermissionScopingDemo() {
  const [state, setState] = useState<State>('perm1');
  const [perm1Granted, setPerm1Granted] = useState(false);
  const [perm2Granted, setPerm2Granted] = useState(false);

  function answerPerm1(granted: boolean) {
    setPerm1Granted(granted);
    setState('perm2');
  }

  function answerPerm2(granted: boolean) {
    setPerm2Granted(granted);
    setState('ready');
  }

  function handleReset() {
    setState('perm1');
    setPerm1Granted(false);
    setPerm2Granted(false);
  }

  const permissions: Permission[] = [
    { label: 'Profile access', granted: perm1Granted },
    { label: 'Save CVs', granted: perm2Granted },
  ];

  return (
    <div className={kit.root}>
      <div className={kit.message}>Help me create a CV for a jobseeker.</div>

      {state === 'perm1' && (
        <>
          <AgentLine>To get started, I need one thing.</AgentLine>
          <PermissionCard
            step={1}
            title="Read jobseeker profile"
            reason="So I can personalize the CV to this person's experience and background."
            skipLabel="Not now"
            onAllow={() => answerPerm1(true)}
            onSkip={() => answerPerm1(false)}
          />
          <p className={kit.feedback}>You can change this any time in settings.</p>
        </>
      )}

      {state === 'perm2' && (
        <>
          <div className={kit.row}>
            <GrantedChip label="Profile access" granted={perm1Granted} />
          </div>
          <AgentLine>One more thing for better results.</AgentLine>
          <PermissionCard
            step={2}
            title="Save generated CVs to this account"
            reason="So you can access, edit and download CVs you've created without starting over."
            skipLabel="Skip for now"
            onAllow={() => answerPerm2(true)}
            onSkip={() => answerPerm2(false)}
          />
          <p className={kit.feedback}>You can change this any time in settings.</p>
        </>
      )}

      {state === 'ready' && (
        <>
          <AgentLine>You're set up. Here's what I can and can't do.</AgentLine>
          <div className={kit.row}>
            {permissions.map((p) => (
              <GrantedChip key={p.label} label={p.label} granted={p.granted} onEnable={handleReset} />
            ))}
          </div>
          <div className={kit.row}>
            <button className={`${kit.btn} ${kit.btnPrimary}`} onClick={handleReset}>
              Start creating CVs
            </button>
          </div>
        </>
      )}
    </div>
  );
}
